// src/pages/Wishlist.js
// Displays the logged-in customer's saved products.
// Fetches full product details for each wishlisted handle from Shopify.
// Guests are redirected to /login — wishlist requires authentication.

import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Heart, ShoppingBag, Trash2, ArrowRight } from 'lucide-react';
import { useWishlist } from '../context/WishlistContext';
import { useAuth } from '../context/AuthContext';
import { useCart, GIFT_BAG_HANDLE } from '../context/CartContext';
import { useCurrency } from '../context/CurrencyContext';
import { getScent, BEST_SELLER_HANDLE, LOW_STOCK, NEW_RELEASE_HANDLES } from '../data/scents';
import usePageTitle from '../hooks/usePageTitle';

const Wishlist = () => {
    usePageTitle('Your Wishlist', 'Your saved MAISON D\'OUD fragrances.');

    const { wishlist, removeFromWishlist } = useWishlist();
    const { isLoggedIn } = useAuth();
    const { addToCart, cart, loading: cartLoading } = useCart();
    const { country, symbol } = useCurrency();
    const navigate = useNavigate();

    // Full product objects fetched from Shopify for each wishlisted handle
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);

    // Tracks which items have just been added to cart (shows brief confirmation)
    const [addedItems, setAddedItems] = useState({});
    const [addPendingId, setAddPendingId] = useState(null);
    const cartBeforeRef = useRef(null);

    // Redirect guests to login page
    useEffect(() => {
        if (!isLoggedIn) {
            navigate('/login');
        }
    }, [isLoggedIn, navigate]);

    // Fetch full product data for each saved handle whenever wishlist or currency changes
    useEffect(() => {
        if (wishlist.length === 0) {
            setProducts([]);
            setLoading(false);
            return;
        }

        setLoading(true);
        Promise.all(wishlist.map(handle => getScent(handle, country)))
            .then(results => {
                // Filter out any handles that no longer exist in Shopify, and the gift bag
                setProducts(results.filter(p => p && p.handle !== GIFT_BAG_HANDLE));
            })
            .catch(console.error)
            .finally(() => setLoading(false));
    }, [wishlist, country]);

    // Only show the "Added!" confirmation once addToCart's mutation has
    // actually landed — detected by the cart object changing identity, since
    // CartContext only calls setCart on a successful add, never on failure.
    useEffect(() => {
        if (!addPendingId || cartLoading) return;
        const id = addPendingId;
        setAddPendingId(null);
        if (cart !== cartBeforeRef.current) {
            setAddedItems(prev => ({ ...prev, [id]: true }));
            setTimeout(() => {
                setAddedItems(prev => ({ ...prev, [id]: false }));
            }, 2000);
        }
    }, [addPendingId, cartLoading, cart]);

    // Add to cart — confirmation is shown once the add is confirmed successful
    const handleAddToCart = (product) => {
        cartBeforeRef.current = cart;
        setAddPendingId(product.id);
        addToCart(product.variantId);
    };

    if (loading) return (
        <div className="bg-background min-h-screen pt-32 flex items-center justify-center">
            <div className="animate-pulse text-muted-foreground tracking-wide">Loading your wishlist...</div>
        </div>
    );

    return (
        <div className="bg-background min-h-screen pt-32 pb-24 px-4 sm:px-6 transition-colors duration-300">
            <div className="max-w-7xl mx-auto">

                {/* Page header with saved count */}
                <div className="text-center mb-12 sm:mb-16">
                    <h1 className="text-4xl sm:text-5xl md:text-6xl font-light text-foreground mb-4 tracking-wide">
                        Your Wishlist
                    </h1>
                    <p className="text-muted-foreground text-base sm:text-lg">
                        {products.length > 0
                            ? `${products.length} saved ${products.length === 1 ? 'fragrance' : 'fragrances'}`
                            : 'Your wishlist is empty.'}
                    </p>
                </div>

                {/* Empty state */}
                {products.length === 0 && (
                    <div className="text-center py-16">
                        <div className="w-16 h-16 bg-[#CEAC8F]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                            <Heart className="w-8 h-8 text-[#CEAC8F]/40" />
                        </div>
                        <p className="text-muted-foreground text-lg mb-2">Nothing saved yet.</p>
                        <p className="text-muted-foreground/60 text-sm mb-8">
                            Tap the heart on any product to save it here.
                        </p>
                        <Link
                            to="/scents"
                            className="inline-flex items-center gap-2 bg-[#CEAC8F] text-black px-8 py-4 rounded-lg font-medium hover:bg-[#d4b896] transition-all hover:gap-4"
                        >
                            Browse Atlas Collection
                            <ArrowRight className="w-5 h-5" />
                        </Link>
                    </div>
                )}

                {/* Wishlist product grid */}
                {products.length > 0 && (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-6 md:gap-8">
                        {products.map((product) => (
                            <div
                                key={product.id}
                                className="group relative bg-card rounded-xl sm:rounded-2xl overflow-hidden border border-border hover:border-[#CEAC8F]/30 transition-all duration-500"
                            >
                                {(NEW_RELEASE_HANDLES.includes(product.handle) || product.handle === BEST_SELLER_HANDLE || LOW_STOCK[product.handle]) && (
                                    <div className="absolute top-3 left-3 z-10 flex flex-col gap-1.5">
                                        {NEW_RELEASE_HANDLES.includes(product.handle) && (
                                            <span className="bg-[#4E2326] text-white text-[10px] font-medium px-2.5 py-1 rounded-full uppercase tracking-widest">
                                                New
                                            </span>
                                        )}
                                        {product.handle === BEST_SELLER_HANDLE && (
                                            <span className="bg-[#CEAC8F] text-black text-[10px] font-medium px-2.5 py-1 rounded-full uppercase tracking-widest">
                                                Best Seller
                                            </span>
                                        )}
                                        {LOW_STOCK[product.handle] && (
                                            <span className="bg-red-600 text-white text-[10px] font-medium px-2.5 py-1 rounded-full uppercase tracking-widest">
                                                Low Stock
                                            </span>
                                        )}
                                    </div>
                                )}
                                {/* Remove from wishlist button */}
                                <button
                                    onClick={() => removeFromWishlist(product.handle)}
                                    className="absolute top-3 right-3 z-10 w-8 h-8 bg-background/80 backdrop-blur-sm rounded-full flex items-center justify-center text-red-400 hover:bg-background transition-all"
                                    aria-label="Remove from wishlist"
                                >
                                    <Trash2 className="w-4 h-4" />
                                </button>

                                {/* Product image — navigates to product page on click */}
                                <Link to={`/product/${product.handle}`}>
                                    <div className="aspect-[3/4] overflow-hidden">
                                        <img
                                            src={product.image}
                                            alt={product.imageAlt}
                                            loading="lazy"
                                            className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-700"
                                        />
                                    </div>
                                </Link>

                                <div className="p-3 sm:p-5">
                                    {product.tags?.length > 0 && (
                                        <p className="text-[#CEAC8F]/70 text-[10px] sm:text-xs tracking-widest mb-1 uppercase">
                                            {product.tags[0]}
                                        </p>
                                    )}
                                    <Link to={`/product/${product.handle}`}>
                                        <h3 className="text-foreground text-sm sm:text-lg font-light mb-1 hover:text-[#CEAC8F] transition-colors">
                                            {product.name}
                                        </h3>
                                    </Link>
                                    {product.handle !== GIFT_BAG_HANDLE && (
                                        <p className="text-muted-foreground/70 text-[10px] sm:text-xs tracking-widest mb-1 uppercase">12ml</p>
                                    )}
                                    <p className="text-[#CEAC8F] text-lg sm:text-xl font-light mb-4">
                                        {symbol}{product.price.toFixed(2)}
                                    </p>

                                    {/* Add to cart — disabled if sold out or a cart mutation is in flight */}
                                    <button
                                        onClick={() => handleAddToCart(product)}
                                        disabled={!product.availableForSale || cartLoading}
                                        className={`w-full flex items-center justify-center gap-2 py-2 rounded-lg text-xs sm:text-sm font-medium transition-all ${
                                            addedItems[product.id]
                                                ? 'bg-green-600 text-white'
                                                : product.availableForSale
                                                    ? 'bg-[#CEAC8F] text-black hover:bg-[#d4b896]'
                                                    : 'bg-muted text-muted-foreground cursor-not-allowed'
                                        }`}
                                    >
                                        <ShoppingBag className="w-3 h-3 sm:w-4 sm:h-4" />
                                        {addedItems[product.id]
                                            ? 'Added!'
                                            : product.availableForSale
                                                ? 'Add to Cart'
                                                : 'Sold Out'}
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Continue shopping link */}
                {products.length > 0 && (
                    <div className="text-center mt-16">
                        <Link
                            to="/scents"
                            className="inline-flex items-center gap-2 text-[#CEAC8F] hover:gap-4 transition-all"
                        >
                            Continue Shopping
                            <ArrowRight className="w-5 h-5" />
                        </Link>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Wishlist;